// Sprint 5 — Client AI Funding Match Engine.
// Client provides income/industry/goals → POST /api/funding/match → top 5
// matched funders (curated + AI-added) with fit_score + rationale.
import { useCallback, useEffect, useState } from "react";
import BrandShell from "../components/BrandShell";
import { apiCall } from "../auth/AuthContext";
import { Loader2, Sparkles, TrendingUp, ExternalLink, Bot, Building2 } from "lucide-react";

export default function ClientFundingMatch() {
  const [annualIncome, setAnnualIncome] = useState("");
  const [industry, setIndustry] = useState("");
  const [desiredAmount, setDesiredAmount] = useState("");
  const [goals, setGoals] = useState("");
  const [running, setRunning] = useState(false);
  const [error, setError] = useState("");
  const [latest, setLatest] = useState(null);
  const [history, setHistory] = useState([]);

  const loadHistory = useCallback(async () => {
    try {
      const d = await apiCall("/api/funding/matches");
      return d.records || [];
    } catch { return []; }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const rows = await loadHistory();
      if (cancelled) return;
      setHistory(rows);
      if (rows.length > 0) setLatest(rows[0]);
    })();
    return () => { cancelled = true; };
  }, [loadHistory]);

  const run = async () => {
    setRunning(true); setError("");
    try {
      const body = {};
      if (annualIncome) body.annual_income = parseFloat(annualIncome);
      if (industry) body.industry = industry;
      if (desiredAmount) body.desired_amount = parseFloat(desiredAmount);
      if (goals) body.goals = goals;
      const d = await apiCall("/api/funding/match", { method: "POST", body: JSON.stringify(body) });
      setLatest(d.result);
      const rows = await loadHistory();
      setHistory(rows);
    } catch (e) {
      setError(e.message || "Match failed");
    } finally { setRunning(false); }
  };

  const matches = latest?.matches || [];
  const aiAdded = latest?.ai_added || [];

  return (
    <BrandShell dashboardChrome>
      <div className="bg-jc-black min-h-[calc(100vh-5rem)]">
        <div className="px-6 md:px-12 py-12 max-w-5xl mx-auto" data-testid="client-funding-match">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-jc-gold mb-2">◆ AI Funding Readiness</p>
        <h1 className="font-display text-jc-white" style={{ fontSize: "clamp(2rem, 4vw, 3.4rem)", lineHeight: 0.95 }}>
          The right funder. The right time. Matched by AI.
        </h1>
        <p className="mt-4 text-jc-muted max-w-2xl">
          We screen our curated funder network against your credit profile and goals, then add AI-recommended options to fill the gaps. Top 5 ranked by fit — matched by AI, decided by you.
        </p>

        {/* Input form */}
        <section className="mt-10 border border-jc-gold/25 bg-jc-navy/40 p-6 rounded-xl" data-testid="funding-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Annual income (USD)" testId="funding-income" type="number" value={annualIncome} onChange={setAnnualIncome} placeholder="65000" />
            <Field label="Desired amount (USD)" testId="funding-amount" type="number" value={desiredAmount} onChange={setDesiredAmount} placeholder="25000" />
            <Field label="Industry" testId="funding-industry" value={industry} onChange={setIndustry} placeholder="e.g., e-commerce, real estate, consulting" />
            <Field label="Use of funds" testId="funding-goals" value={goals} onChange={setGoals} placeholder="working capital, equipment, debt consolidation…" />
          </div>
          {error && <p className="mt-3 text-sm text-red-300" data-testid="funding-error">{error}</p>}
          <button data-testid="funding-run-btn" disabled={running} onClick={run} className="mt-5 jc-btn jc-btn-primary disabled:opacity-50">
            {running ? <><Loader2 size={14} className="animate-spin"/> Matching with AI…</> : <><Sparkles size={14}/> Find My Matches</>}
          </button>
        </section>

        {/* Results */}
        {latest && (
          <section className="mt-10" data-testid="funding-results">
            <div className="flex items-center gap-3 mb-4">
              <TrendingUp size={18} className="text-jc-gold"/>
              <h2 className="font-display text-2xl text-jc-white">Your matches</h2>
              <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-jc-muted">{latest.curated_count} funders considered · {latest.filtered_count} pre-qualified</span>
            </div>
            {matches.length === 0 && aiAdded.length === 0 ? (
              <p className="text-sm text-jc-muted">No qualifying funders in our network. Your specialist will reach out with a custom plan.</p>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {matches.map((m, i) => <MatchCard key={`m-${i}`} m={m} rank={i + 1}/>)}
                {aiAdded.map((m, i) => <MatchCard key={`a-${i}`} m={m} rank={matches.length + i + 1} aiAdded/>)}
              </div>
            )}
          </section>
        )}

        {history.length > 1 && (
          <section className="mt-10">
            <h2 className="font-display text-lg text-jc-white mb-3">Previous runs</h2>
            <ul className="space-y-2 text-sm">
              {history.slice(0, 10).map((h) => (
                <li key={h.id} className="flex items-center justify-between border border-jc-gold/15 px-4 py-2">
                  <span className="text-jc-white">{new Date(h.created_at).toLocaleString()}</span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-jc-muted">{(h.matches || []).length} matches</span>
                </li>
              ))}
            </ul>
          </section>
        )}
        </div>
      </div>
    </BrandShell>
  );
}

function Field({ label, value, onChange, placeholder, type = "text", testId }) {
  return (
    <label className="block">
      <span className="text-[11px] font-mono uppercase tracking-[0.22em] text-jc-muted">{label}</span>
      <input data-testid={testId} type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="mt-1.5 w-full bg-jc-black border border-jc-gold/30 text-jc-white px-3 py-2.5 text-sm focus:outline-none focus:border-jc-gold"/>
    </label>
  );
}

function MatchCard({ m, rank, aiAdded }) {
  const score = m.fit_score ?? 0;
  return (
    <div className="border border-jc-gold/20 bg-jc-black/40 p-5 rounded-xl" data-testid={`funding-match-${rank}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-jc-gold">◆ Rank #{rank}</p>
          <h3 className="mt-1 font-display text-lg text-jc-white truncate">{m.funder_name}</h3>
          <p className="text-[11px] font-mono uppercase tracking-[0.18em] text-jc-muted mt-1 flex items-center gap-2">
            {aiAdded ? <><Bot size={11}/> AI-recommended</> : <><Building2 size={11}/> Curated · {m.funder_type || "—"}</>}
          </p>
        </div>
        <div className="text-right">
          <p className="font-display text-2xl text-jc-gold">{score}</p>
          <p className="text-[10px] font-mono uppercase tracking-[0.22em] text-jc-muted">fit score</p>
        </div>
      </div>
      <div className="mt-3 h-1 bg-jc-gold/10 rounded">
        <div className="h-1 bg-jc-gold rounded" style={{ width: `${Math.min(100, Math.max(0, score))}%` }}/>
      </div>
      <p className="mt-3 text-sm text-jc-white leading-relaxed">{m.rationale}</p>
      {m.next_step && <p className="mt-2 text-xs text-jc-muted"><span className="text-jc-gold">Next:</span> {m.next_step}</p>}
      {m.application_url && (
        <a href={m.application_url} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.18em] text-jc-gold hover:underline" data-testid={`funding-apply-${rank}`}>
          <ExternalLink size={11}/> Apply
        </a>
      )}
    </div>
  );
}
